import { useState } from 'react';
import { 
    Trash2, 
    Pen, 
    Copy,
    ChevronDown, 
    ChevronUp,
    Clock,
    RotateCcw,
    Target,
    List
} from 'lucide-react';

type DrillEntryProps = {
    skillsList: any[];
    drill: any;
    setEditing: (drill: any) => void;
    duplicate: (drill: any) => void;
    remove: (id: string) => void;
};

const DrillEntry: React.FC<DrillEntryProps> = ({ skillsList, drill, setEditing, duplicate, remove }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const thisDrill = {
        ...drill,
        skills: drill.drill.skills.map((skill: any) => {
            return skillsList.filter((value) => value.id === skill)[0];
        })
    };

    return (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl mb-4">
            {/* Header */}
            <div className="bg-gradient-to-r from-violet-500 to-violet-600 p-4 relative">
                <div className="absolute inset-0 bg-white opacity-10"></div>
                <div className="relative flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <Target className="w-4 h-4" />
                        <div>
                            <h3 className="font-bold text-lg">
                                Drill
                            </h3>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => setEditing(drill)}
                            className="w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                            title="Edit"
                        >
                            <Pen className="w-4 h-4 text-gray-800 hover:text-gray-900" />
                        </button>

                        <button
                            onClick={() => duplicate(drill)}
                            className="w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                            title="Duplicate"
                        >
                            <Copy className="w-4 h-4 text-gray-800 hover:text-gray-900" />
                        </button>

                        <button
                            onClick={() => remove(drill.drill.drill_id)}
                            className="w-8 h-8 text-white bg-red-500 bg-opacity-80 hover:bg-opacity-100 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                            title="Delete"
                        >
                            <Trash2 className="w-4 h-4 text-white" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="p-6">
                {/* Drill Name */}
                <div className="mb-4">
                    <h4 className="text-xl font-semibold text-gray-800 leading-tight">
                        {drill.name.length > 55 ? drill.name.slice(0, 55) + '...' : drill.name}
                    </h4>
                </div>

                {/* Quick Stats */}
                {!isExpanded && (
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-sm text-gray-500">Length</div>
                            <div className="text-lg font-bold text-gray-800">
                                {drill.drill.length}
                            </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-sm text-gray-500">Repetitions</div>
                            <div className="text-lg font-bold text-gray-800">
                                {drill.drill.repetitions}
                            </div>
                        </div>
                    </div>
                )}

                {/* Expanded Details */}
                <div className={`transition-all duration-300 overflow-hidden ${
                    isExpanded ? 'max-h-none opacity-100' : 'max-h-0 opacity-0'
                }`}>
                    <div className="space-y-4 pt-4 border-t border-gray-100">
                        {/* Drill Metrics */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-blue-50 rounded-lg p-4 flex items-center space-x-3">
                                <Clock className="w-5 h-5 text-blue-500" />
                                <div>
                                    <div className="text-sm text-blue-600 font-medium">Length</div>
                                    <div className="text-lg font-bold text-blue-800">
                                        {drill.drill.length}
                                    </div>
                                </div>
                            </div>

                            <div className="bg-green-50 rounded-lg p-4 flex items-center space-x-3">
                                <RotateCcw className="w-5 h-5 text-green-500" />
                                <div>
                                    <div className="text-sm text-green-600 font-medium">Repetitions</div>
                                    <div className="text-lg font-bold text-green-800">
                                        {drill.drill.repetitions}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Skills List */}
                        <div className="bg-indigo-50 rounded-lg p-4">
                            <div className="flex items-center space-x-2 mb-3">
                                <List className="w-5 h-5 text-indigo-600" />
                                <span className="font-semibold text-indigo-800 text-sm">Skills ({thisDrill.skills.length})</span>
                            </div>
                            <div className="space-y-2">
                                {thisDrill.skills.map((skill: any, index: number) => (
                                    <div key={index} className="bg-white rounded-lg p-3 border border-indigo-100">
                                        <div className="flex items-center space-x-3">
                                            <div className="w-6 h-6 bg-indigo-200 rounded-full flex items-center justify-center">
                                                <span className="text-xs font-bold text-indigo-700">
                                                    {index + 1}
                                                </span>
                                            </div>
                                            <span className="text-base font-medium text-indigo-800">
                                                {skill.name}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Toggle Button */}
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="w-full mt-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 font-medium text-gray-700"
                >
                    <span className="text-base">{isExpanded ? 'Show Less' : 'Show More'}</span>
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
            </div>
        </div>
    );
};

export default DrillEntry;